// ══════════════════════════════════════════════════════════════════════════════
// SCORING CORE — the single implementation of the deal maths.
//
// Imported by BOTH the browser (src/lib/scoring-engine.ts, which adds the
// sessionStorage-backed threshold overrides and context builders) and the
// deal-intelligence edge function, so a deal scores identically wherever it is
// evaluated. Two copies of this maths is exactly the divergence this module
// exists to prevent.
//
// Rules for this file:
//   • No imports. It must load in Deno and in the browser unchanged.
//   • No browser globals — thresholds arrive as an argument, never from storage.
//   • Pure functions only: same input, same output, no I/O.
// ══════════════════════════════════════════════════════════════════════════════

// ══════════════════════════════════════════════════════════════════════════════
// Brickshare Institutional Scoring Engine v2
// 4-Category Model: Returns (40) + Debt Safety (25) + Execution Risk (20) + Exit & Market Risk (15)
// ══════════════════════════════════════════════════════════════════════════════

// ── Thresholds (loaded from user settings or defaults) ──

export interface ScoringThresholds {
  target_irr: number;
  target_coc: number;
  target_dscr: number;
  proceed_cutoff: number;
  caution_cutoff: number;
  stabilized_vacancy_default: number;
  selling_costs_pct: number;
}

export const DEFAULT_THRESHOLDS: ScoringThresholds = {
  target_irr: 15,
  target_coc: 8,
  target_dscr: 1.25,
  proceed_cutoff: 75,
  caution_cutoff: 60,
  stabilized_vacancy_default: 5,
  selling_costs_pct: 3,
};



// ── Input Interface ──

export interface ScoringInput {
  // Strategy Gate
  unit_count?: number;
  occupancy_current?: number;       // % (0-100)
  year_built?: number;
  market_type?: string;             // "primary" | "secondary" | "tertiary"
  hold_period?: string;             // "3" | "4" | "5" | "7+"
  exit_strategy?: string;
  target_irr_band?: string;         // "12-14" | "14-16" | "16-18" | "18+"
  operational_value_add?: string[];
  physical_value_add?: string[];

  // Quick Screen - Acquisition
  purchase_price?: number;
  capex_total?: number;             // total renovation/CapEx
  capex_per_unit?: number;
  closing_costs_pct?: number;       // default 2%

  // Quick Screen - Income
  avg_in_place_rent?: number;       // monthly per unit
  stabilized_rent?: number;         // monthly per unit (or derived from premium)
  rent_premium_pct?: number;        // % above in-place
  other_income?: number;            // annual
  gross_potential_rent?: number;    // annual total
  baseline_total_income?: number;   // annual T12 effective income / EGI

  // Quick Screen - Expenses
  expense_ratio?: number;           // %
  total_operating_expenses?: number;
  baseline_expenses?: number;       // annual T12 operating expenses
  property_taxes?: number;
  insurance?: number;
  per_unit_expenses?: number;

  // Quick Screen - Vacancy
  vacancy_rate?: number;            // stabilized vacancy %

  // Quick Screen - Debt
  ltv?: number;                     // %
  interest_rate?: number;           // %
  amortization?: number;            // years
  loan_amount?: number;

  // Assumptions — annual growth applied after stabilization
  rent_growth_annual?: number;      // % per year, income
  expense_growth_annual?: number;   // % per year, operating expenses

  // Attached by the caller from the deal's metro market_data row (never by
  // buildScoringInputFromObject — it is market fact, not deal context).
  // Calibrates the sensitivity stress grid only; the base score ignores it.
  market_calibration?: MarketCalibration | null;

  // Quick Screen - Exit
  exit_cap?: number;                // %
  selling_costs_pct?: number;       // default 3%

  // Legacy / document-extracted
  net_operating_income?: number;
  market_rent_estimate?: number;
  avg_sqft?: number;
  capex_reserves?: number;          // legacy alias for capex_total
  cap_rate?: number;                // going-in, computed
  dscr?: number;                    // legacy pre-computed
  cash_on_cash?: number;            // legacy pre-computed
}

// ── Output Interfaces ──

export interface PillarInput {
  label: string;
  key: string;
  provided: boolean;
  value?: string | number;
  source: "context" | "document" | "manual" | "computed" | "missing";
  impact: "high" | "medium" | "low";
  improvementHint?: string;
}

export interface SubScore {
  name: string;
  key: string;
  score: number;
  maxPoints: number;
  details?: string;
}

export interface PillarScore {
  name: string;
  key: string;
  score: number;       // 0–100 (normalized)
  rawScore: number;    // 0–maxPoints
  maxPossible: number; // category max (40, 25, 20, or 15)
  weight: number;      // 0–1
  available: boolean;
  inputs: PillarInput[];
  subScores: SubScore[];
}

export interface RiskFlag {
  text: string;
  severity: "major" | "minor";
  category: string;
  nextStep?: string;
}

// ══════════════════════════════════════════════════════════════════════════════
// MARKET CALIBRATION
//
// The stress grid used to be one-size-fits-all: rent −10%, exit cap +50/+100bps
// for every deal, whether its metro's rents were falling 1.8% a year (San
// Antonio) or rising 5.2% (Chicago). The same nominal stress is a mild bump in
// one market and beyond anything observed in the other.
//
// When a deal carries market calibration, the grid anchors to what its metro
// actually did:
//   • Rent stress = clamp(10 − rent_growth_yoy, 5, 15), so a falling market is
//     stressed harder and a rising one is not punished past observed reality.
//     Floored at 5% — no market is ever assumed too strong to miss rents.
//   • Exit-cap stress deepens to +75/+125bps when the metro's cap-rate trend is
//     rising. It never softens below the standard +50/+100: cap expansion is
//     the risk that kills exits, and a favourable trend claim is too weak a
//     signal to bet the downside case on. The asymmetry is deliberate.
//
// Calibration only ever adjusts the STRESS TESTS — never the base score. And
// with no calibration (metro missing, data absent, confidence too low) the
// grid is exactly the standard one, so every existing score is unchanged.
// ══════════════════════════════════════════════════════════════════════════════

export interface MarketCalibration {
  metro: string;
  rent_growth_yoy: number | null;
  cap_rate_trend: "up" | "down" | "flat" | null;
  cap_rate: number | null;
}

/** What the grid actually applied, echoed for the memo and for review. */
export interface SensitivityCalibrationEcho {
  metro: string;
  rent_stress_pct: number;
  exit_cap_bps: [number, number];
  notes: string[];
}

/** Minimum confidence a market_data rent figure needs before it may move the
 *  stress grid. Below this the standard grid applies — an uncertain number
 *  should not be softening anyone's downside case. */
const CALIBRATION_MIN_CONFIDENCE = 50;

/**
 * Build a calibration from a market_data / market_data_readable row.
 *
 * Shared by the orchestrator and the browser so the two cannot derive
 * different stress grids from the same row — sensitivity feeds the global
 * break detector, which can force a verdict, so a divergence here would be
 * the two-engines bug all over again. Numerics are coerced because PostgREST
 * returns numeric columns as strings.
 */
export function buildMarketCalibration(row: Record<string, unknown> | null | undefined): MarketCalibration | null {
  if (!row || typeof row !== "object") return null;
  const metro = typeof row.metro === "string" ? row.metro.trim() : "";
  if (!metro) return null;

  const num = (v: unknown): number | null => {
    if (v == null || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const rentConfidence = num(row.rent_confidence);
  const rentGrowth = num(row.rent_growth_yoy);
  const trendRaw = typeof row.cap_rate_trend === "string" ? row.cap_rate_trend.trim().toLowerCase() : null;

  return {
    metro,
    // An uncertain rent figure is withheld entirely rather than dampened —
    // partial trust in a number the grid uses to soften stress is not trust.
    rent_growth_yoy: rentConfidence == null || rentConfidence >= CALIBRATION_MIN_CONFIDENCE ? rentGrowth : null,
    cap_rate_trend: trendRaw === "up" || trendRaw === "down" || trendRaw === "flat" ? trendRaw : null,
    cap_rate: num(row.cap_rate),
  };
}

export interface SensitivityScenario {
  label: string;
  description: string;
  score: number;
  irr: number | null;
  dscr: number | null;
  verdictShift: string;
}

export interface ConfidenceResult {
  level: "High" | "Medium" | "Low";
  score: number;          // 0-100
  completeness: number;   // 0-50
  realism: number;        // 0-30
  stability: number;      // 0-20
  details: string[];
}

export interface DerivedMetrics {
  baseline_noi: number;
  baseline_income: number;
  baseline_expenses: number;
  expense_ratio: number;
  noi_in_place: number;
  noi_stabilized: number;
  egi_in_place: number;
  egi_stabilized: number;
  annual_debt_service: number;
  dscr: number;
  /**
   * NOI over loan amount, as a percentage. The metric lenders have actually
   * sized on since 2008, because unlike DSCR and LTV it is immune to both the
   * rate and the amortization schedule — you cannot flatter it with a longer
   * amortization or an interest-only period. Agency deals typically clear
   * around 7%; banks want 8-10%. Zero when there is no debt.
   */
  debt_yield: number;
  annual_cash_flow: number;
  cash_on_cash: number;
  total_cash_invested: number;
  cap_rate_going_in: number;
  cap_rate_stabilized: number;
  equity_created: number;
  equity_created_pct: number;
  proxy_irr: number;
  /**
   * The same deal bought for cash — the return before any financing.
   *
   * Levered IRR rewards leverage, so a core-plus asset carrying 75% debt can
   * present as value-add without a single thing about the property changing.
   * The gap between this figure and `proxy_irr` is the share of the return that
   * is borrowed rather than earned, and it is the number that says whether this
   * is good real estate as opposed to a good loan.
   */
  unlevered_irr: number;
  /**
   * Total distributions over the hold divided by the equity that bought them,
   * e.g. 1.70x. Read from the same levered cash-flow ladder the IRR is solved
   * over, so the two can never describe different cash.
   *
   * NaN when that ladder cannot be built at all. Note the deliberate asymmetry
   * with `proxy_irr`: the IRR carries the extra requirement that Newton's
   * method converge inside a believable band, so a catastrophic deal can report
   * a multiple (a negative one — you lose the equity and then some) while its
   * IRR is withheld as unreliable. Summing a ladder is always sound; solving it
   * for a rate is not.
   */
  equity_multiple: number;
  breakeven_occupancy: number;
  value_at_stabilization: number;
  exit_value: number;               // value at the END of the hold, NOI grown
  loan_balance_at_exit: number;
  loan_amount: number;
  down_payment: number;
  has_t12_data: boolean;
  financial_model_valid: boolean;
  financial_model_issues: string[];
}

export function isPositiveFinite(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

export interface ScoringResult {
  overall_score: number;
  overall_max: number;
  pillars_scored: number;
  pillars_total: number;
  pillars: PillarScore[];
  missing_fields: PillarInput[];
  verdict: "GO" | "NO-GO" | "MAYBE";
  verdict_label: string;
  verdict_note: string;
  risk_level: "Low" | "Medium" | "High" | "Very High";
  confidence: ConfidenceResult;
  flags: RiskFlag[];
  sensitivity: SensitivityScenario[];
  /** Non-null when the stress grid was calibrated to the deal's metro. */
  sensitivity_calibration: SensitivityCalibrationEcho | null;
  derived: DerivedMetrics;
  drivers: { positives: string[]; risks: string[] };
  actions: { text: string; priority: "high" | "medium" | "low" }[];
}

// ══════════════════════════════════════════════════════════════════════════════
// PROXY IRR (Simplified multi-year DCF with Newton's method)
// ══════════════════════════════════════════════════════════════════════════════

interface ProxyIRRArgs {
  totalCashInvested: number;
  noiInPlace: number;
  noiStabilized: number;
  /** Stabilized income and expenses, grown separately after stabilization. */
  egiStabilized: number;
  expensesStabilized: number;
  rentGrowth: number;        // fraction, e.g. 0.0325
  expenseGrowth: number;     // fraction
  annualDebtService: number;
  exitValue: number;         // gross sale price at the end of the hold
  loanAmount: number;        // principal drawn at close
  loanBalanceAtExit: number; // principal still owed at sale
  sellingCostsPct: number;
  holdYears: number;
}

/**
 * The levered cash-flow ladder: equity out at close, levered cash flow each
 * year of the hold, and the net sale folded into the final year.
 *
 * This is the single source of truth for the returns model. Both the IRR and
 * the equity multiple are read from it, so the two cannot drift apart — a
 * second ladder built to serve the multiple would be the two-engines bug in
 * miniature.
 *
 * Returns null when the inputs cannot support a trustworthy return profile;
 * callers surface that as "hide this metric" rather than a made-up number.
 */
function buildCashFlows(args: ProxyIRRArgs, levered: boolean): number[] | null {
  const {
    totalCashInvested, noiInPlace, noiStabilized,
    egiStabilized, expensesStabilized, rentGrowth, expenseGrowth,
    exitValue, loanAmount, loanBalanceAtExit, sellingCostsPct, holdYears,
  } = args;
  // Unlevered means the debt never existed: the buyer funds the whole basis at
  // close, keeps every dollar of NOI, and hands nothing back at sale.
  const annualDebtService = levered ? args.annualDebtService : 0;
  const equityOut = levered ? totalCashInvested : totalCashInvested + Math.max(0, loanAmount);
  const payoffAtExit = levered ? loanBalanceAtExit : 0;
  const valueAtStabilization = exitValue;
  // Validity guards — return null to signal "insufficient data, hide the
  // metric" rather than letting Newton's method clamp to 500%.
  if (equityOut <= 0) return null;
  if (!Number.isFinite(noiStabilized) || noiStabilized <= 0) return null;
  if (!Number.isFinite(valueAtStabilization) || valueAtStabilization <= 0) return null;
  // If equity is wildly small relative to NOI, the model is unreliable
  // (cash-on-cash > 100% in year 1 is non-sensical for real estate).
  if (noiStabilized / equityOut > 1.5) return null;

  const cashFlows: number[] = [-equityOut];
  const stabYear = Math.max(1, Math.ceil(holdYears * 0.4));
  const cfInPlace = noiInPlace - annualDebtService;
  const cfStabilized = noiStabilized - annualDebtService;

  const canGrow = egiStabilized > 0 && expensesStabilized > 0;

  for (let yr = 1; yr <= holdYears; yr++) {
    let cf: number;
    if (yr <= stabYear) {
      // Ramp from in-place to stabilized while the business plan executes.
      //
      // Year one is the NOI you bought, not a point part-way up the ramp. The
      // old form (pct = yr / stabYear) put year one half-way to stabilised, so
      // the model never spent a single year at the going-in NOI — and the
      // published cash-on-cash, which is computed off in-place NOI, disagreed
      // with the ladder's own year one by 34% on the benchmark deal.
      const pct = stabYear > 1 ? (yr - 1) / (stabYear - 1) : 1;
      cf = cfInPlace + (cfStabilized - cfInPlace) * pct;
    } else if (canGrow) {
      // Past stabilization income and expenses compound at their own rates.
      const yearsGrown = yr - stabYear;
      const noi = egiStabilized * Math.pow(1 + rentGrowth, yearsGrown)
        - expensesStabilized * Math.pow(1 + expenseGrowth, yearsGrown);
      cf = noi - annualDebtService;
    } else {
      cf = cfStabilized;
    }
    if (yr === holdYears) {
      const grossSale = valueAtStabilization;
      const sellingCosts = grossSale * sellingCostsPct;
      // Only the principal still outstanding is repaid — amortization during
      // the hold belongs to the seller.
      const netProceeds = grossSale - sellingCosts - payoffAtExit;
      cf += netProceeds;
    }
    cashFlows.push(cf);
  }
  return cashFlows;
}

/** The levered ladder — what the equity cheque actually experiences. */
const buildLeveredCashFlows = (args: ProxyIRRArgs) => buildCashFlows(args, true);

/**
 * The same deal bought for cash.
 *
 * An underwriter reaches for this to separate the asset from the financing.
 * A levered IRR rewards leverage, so a core-plus asset with 75% debt can
 * present as value-add without a single thing about the property changing —
 * which is precisely how a return target gets met on paper by borrowing more.
 * The unlevered figure is the one that describes the real estate.
 */
const buildUnleveredCashFlows = (args: ProxyIRRArgs) => buildCashFlows(args, false);

function solveIRR(cashFlows: number[]): number {

  const npv = (r: number) => {
    let sum = 0;
    for (let i = 0; i < cashFlows.length; i++) sum += cashFlows[i] / Math.pow(1 + r, i);
    return sum;
  };
  const dnpv = (r: number) => {
    let sum = 0;
    for (let i = 1; i < cashFlows.length; i++) sum += -i * cashFlows[i] / Math.pow(1 + r, i + 1);
    return sum;
  };

  let rate = 0.12;
  let converged = false;
  for (let iter = 0; iter < 50; iter++) {
    const f = npv(rate);
    const df = dnpv(rate);
    if (Math.abs(df) < 1e-10) break;
    const newRate = rate - f / df;
    if (newRate < -0.5) { rate = -0.5; break; }
    if (newRate > 5) { rate = 5; break; }
    if (Math.abs(newRate - rate) < 1e-8) { rate = newRate; converged = true; break; }
    rate = newRate;
  }

  const irrPct = rate * 100;
  // Reject placeholder/unrealistic outputs — caller treats NaN as "hide IRR".
  if (!converged && Math.abs(irrPct) >= 49) return NaN;
  if (Math.abs(irrPct) > 50) return NaN;
  return irrPct;
}

/** Levered IRR — the return on the equity actually put in. */
function computeProxyIRR(args: ProxyIRRArgs): number {
  const cashFlows = buildLeveredCashFlows(args);
  return cashFlows ? solveIRR(cashFlows) : NaN;
}

/**
 * Unlevered IRR — the return the property produces before any financing.
 *
 * Reported alongside the levered figure because the two answer different
 * questions. Levered asks "what does my equity earn?"; unlevered asks "is this
 * good real estate?". A deal can clear a levered target purely on debt, and the
 * gap between the two figures is the portion of the return that is borrowed
 * rather than earned.
 */
function computeUnleveredIRR(args: ProxyIRRArgs): number {
  const cashFlows = buildUnleveredCashFlows(args);
  return cashFlows ? solveIRR(cashFlows) : NaN;
}

/**
 * Equity multiple — total distributions over the hold divided by the equity
 * that bought them. An underwriter quotes this alongside the IRR because the
 * IRR alone can be flattered by timing: front-loaded cash raises the rate
 * without raising what actually comes back.
 *
 * Read from the same ladder as the IRR, so a deal cannot report a healthy
 * return and a poor multiple that disagree about the same cash.
 *
 * `cashFlows[0]` is the equity outflow; everything after it is what comes
 * back, including the net sale folded into the final year. A year of negative
 * levered cash flow therefore *reduces* the multiple rather than being treated
 * as a fresh capital call — the engine has no concept of a capital call, and
 * inventing one here would silently change the denominator.
 */
function computeEquityMultiple(args: ProxyIRRArgs): number {
  const cashFlows = buildLeveredCashFlows(args);
  if (!cashFlows) return NaN;
  const distributions = cashFlows.slice(1).reduce((sum, cf) => sum + cf, 0);
  return distributions / args.totalCashInvested;
}

// ══════════════════════════════════════════════════════════════════════════════
// DERIVED METRICS CALCULATOR
// ══════════════════════════════════════════════════════════════════════════════

function computeAnnualDebtService(loanAmount: number, interestRatePct: number, amortizationYears: number): number {
  if (loanAmount <= 0 || interestRatePct <= 0 || amortizationYears <= 0) return 0;
  const monthlyRate = interestRatePct / 100 / 12;
  const nPayments = amortizationYears * 12;
  const monthlyPayment = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, nPayments))
    / (Math.pow(1 + monthlyRate, nPayments) - 1);
  return Number.isFinite(monthlyPayment) ? monthlyPayment * 12 : 0;
}

/**
 * Remaining principal after `yearsElapsed` of level-payment amortization.
 * Straight amortization only: the wizard does not capture interest-only
 * periods today, so modelling one would invent a term the user never gave us.
 */
function computeLoanBalance(
  loanAmount: number,
  interestRatePct: number,
  amortizationYears: number,
  yearsElapsed: number,
): number {
  if (loanAmount <= 0) return 0;
  const r = (interestRatePct || 0) / 100 / 12;
  const n = (amortizationYears || 30) * 12;
  const elapsed = Math.min(Math.max(0, Math.round(yearsElapsed * 12)), n);
  if (r <= 0) return Math.max(0, loanAmount * (1 - elapsed / n));
  const pmt = loanAmount * r / (1 - Math.pow(1 + r, -n));
  const balance = loanAmount * Math.pow(1 + r, elapsed) - pmt * ((Math.pow(1 + r, elapsed) - 1) / r);
  return Math.max(0, balance);
}

function computeDerivedMetrics(input: ScoringInput): DerivedMetrics {
  const units = input.unit_count || 0;
  const pp = input.purchase_price || 0;
  const capex = input.capex_total || input.capex_reserves || 0;
  const closingPct = (input.closing_costs_pct || 2) / 100;
  const closingCosts = pp * closingPct;

  // Income
  const inPlaceRent = input.avg_in_place_rent || 0;
  const stabilizedRent = input.stabilized_rent
    || (inPlaceRent && input.rent_premium_pct ? inPlaceRent * (1 + input.rent_premium_pct / 100) : 0)
    || (input.market_rent_estimate || 0);
  const otherIncome = input.other_income || 0;

  const grossPotentialIncome_inPlace = units > 0 ? (inPlaceRent * units * 12) + otherIncome
    : (input.gross_potential_rent || 0);
  const grossPotentialIncome_stab = units > 0 && stabilizedRent > 0
    ? (stabilizedRent * units * 12) + otherIncome
    : grossPotentialIncome_inPlace;

  const vacancyPct = (input.vacancy_rate || 5) / 100;
  const directBaselineIncome = input.baseline_total_income || 0;
  const egi_in_place = directBaselineIncome > 0
    ? directBaselineIncome
    : grossPotentialIncome_inPlace * (1 - vacancyPct);
  const egi_stabilized = grossPotentialIncome_stab > 0
    ? grossPotentialIncome_stab * (1 - vacancyPct)
    : egi_in_place;

  const reportedNoi = input.net_operating_income || 0;

  // Expenses — single grounded source for baseline display, expense ratio, DSCR,
  // and sensitivity propagation. We prefer T12-grounded expenses, then derive
  // from reported NOI where possible, then fall back to modeled ratios.
  let baselineExpenses = input.baseline_expenses || input.total_operating_expenses || 0;
  if (!baselineExpenses && directBaselineIncome > 0 && reportedNoi > 0) {
    baselineExpenses = Math.max(0, directBaselineIncome - reportedNoi);
  }
  if (!baselineExpenses && input.expense_ratio && egi_in_place > 0) {
    baselineExpenses = egi_in_place * (input.expense_ratio / 100);
  }
  if (!baselineExpenses && input.expense_ratio && grossPotentialIncome_inPlace > 0) {
    baselineExpenses = grossPotentialIncome_inPlace * (input.expense_ratio / 100);
  }
  if (!baselineExpenses && input.per_unit_expenses && units > 0) {
    baselineExpenses = input.per_unit_expenses * units;
  }

  const canDeriveBaselineNoi = egi_in_place > 0 && baselineExpenses > 0;
  const baseline_noi = canDeriveBaselineNoi
    ? (egi_in_place - baselineExpenses)
    : (reportedNoi > 0 ? reportedNoi : Math.max(0, egi_in_place - baselineExpenses));
  const noi_in_place = baseline_noi;
  const noi_stabilized = egi_stabilized > 0 && baselineExpenses > 0
    ? (egi_stabilized - baselineExpenses)
    : baseline_noi;
  const expense_ratio = baselineExpenses > 0 && egi_in_place > 0
    ? (baselineExpenses / egi_in_place) * 100
    : (input.expense_ratio || 0);

  // Debt
  const ltvPct = input.ltv ? input.ltv / 100 : (input.loan_amount && pp > 0 ? input.loan_amount / pp : 0);
  const loanAmt = input.loan_amount || (pp * ltvPct);
  const amortYears = input.amortization || 30;
  const annual_debt_service = computeAnnualDebtService(loanAmt, input.interest_rate || 0, amortYears);

  const dscr = annual_debt_service > 0 ? noi_in_place / annual_debt_service : 0;
  const debt_yield = loanAmt > 0 ? (noi_in_place / loanAmt) * 100 : 0;
  const annual_cash_flow = noi_in_place - annual_debt_service;

  const down_payment = pp - loanAmt;
  const total_cash_invested = down_payment + closingCosts + capex;
  const cash_on_cash = total_cash_invested > 0 ? (annual_cash_flow / total_cash_invested) * 100 : 0;

  const cap_rate_going_in = pp > 0 ? (noi_in_place / pp) * 100 : (input.cap_rate || 0);
  const cap_rate_stabilized = pp > 0 && noi_stabilized > 0 ? (noi_stabilized / pp) * 100 : 0;

  // Exit / Equity Created
  const exitCap = (input.exit_cap || cap_rate_going_in) / 100;
  const value_at_stabilization = exitCap > 0 ? noi_stabilized / exitCap : 0;
  const totalBasis = pp + capex;
  const equity_created = value_at_stabilization - totalBasis;
  const equity_created_pct = totalBasis > 0 ? (equity_created / totalBasis) * 100 : 0;

  // Proxy IRR via simplified DCF with Newton's method
  const holdYears = parseFloat(input.hold_period || "5") || 5;
  const sellingCostsPct = (input.selling_costs_pct || 3) / 100;
  const rentGrowth = (input.rent_growth_annual ?? 0) / 100;
  const expenseGrowth = (input.expense_growth_annual ?? 0) / 100;

  // The sale happens at the END of the hold, so it is priced off the NOI the
  // property will actually be producing then — not the NOI at stabilization.
  // `value_at_stabilization` keeps its own meaning: value created by executing
  // the business plan, which is what equity_created measures.
  const stabYearForExit = Math.max(1, Math.ceil(holdYears * 0.4));
  const yearsGrownToExit = Math.max(0, holdYears - stabYearForExit);
  const noi_at_exit = (egi_stabilized > 0 && baselineExpenses > 0)
    ? egi_stabilized * Math.pow(1 + rentGrowth, yearsGrownToExit)
      - baselineExpenses * Math.pow(1 + expenseGrowth, yearsGrownToExit)
    : noi_stabilized;
  const exit_value = exitCap > 0 && noi_at_exit > 0 ? noi_at_exit / exitCap : value_at_stabilization;
  const loan_balance_at_exit = computeLoanBalance(
    loanAmt, input.interest_rate || 0, amortYears, holdYears,
  );

  // One description of the returns model, read two ways. Both metrics derive
  // from the identical ladder in buildLeveredCashFlows, which is what stops the
  // IRR and the multiple from ever telling the user two different stories.
  const returnsModel: ProxyIRRArgs = {
    totalCashInvested: total_cash_invested,
    noiInPlace: noi_in_place,
    noiStabilized: noi_stabilized,
    egiStabilized: egi_stabilized,
    expensesStabilized: baselineExpenses,
    rentGrowth,
    expenseGrowth,
    annualDebtService: annual_debt_service,
    exitValue: exit_value,
    loanAmount: loanAmt,
    loanBalanceAtExit: loan_balance_at_exit,
    sellingCostsPct,
    holdYears,
  };
  const proxy_irr = computeProxyIRR(returnsModel);
  const unlevered_irr = computeUnleveredIRR(returnsModel);
  const equity_multiple = computeEquityMultiple(returnsModel);

  // Breakeven Occupancy
  const gpi = grossPotentialIncome_inPlace > 0 ? grossPotentialIncome_inPlace : 1;
  const breakeven_occupancy = ((baselineExpenses + annual_debt_service) / gpi) * 100;

  const has_t12_data = directBaselineIncome > 0 || baselineExpenses > 0 || reportedNoi > 0;
  const financial_model_issues: string[] = [];
  if (has_t12_data && baseline_noi <= 0) {
    financial_model_issues.push("NOI is 0 even though T12-backed financial data exists.");
  }
  if (expense_ratio <= 0) {
    financial_model_issues.push("Expense ratio could not be derived from the financial model.");
  }
  if (has_t12_data && annual_debt_service > 0 && dscr <= 0) {
    financial_model_issues.push("DSCR could not be grounded from the T12-backed NOI and debt service.");
  }
  const financial_model_valid = financial_model_issues.length === 0;

  return {
    baseline_noi,
    baseline_income: egi_in_place,
    baseline_expenses: baselineExpenses,
    expense_ratio,
    noi_in_place, noi_stabilized, egi_in_place, egi_stabilized,
    annual_debt_service, dscr, debt_yield, annual_cash_flow, cash_on_cash,
    total_cash_invested, cap_rate_going_in, cap_rate_stabilized,
    equity_created, equity_created_pct, proxy_irr, unlevered_irr, equity_multiple, breakeven_occupancy,
    value_at_stabilization, exit_value, loan_balance_at_exit,
    loan_amount: loanAmt, down_payment,
    has_t12_data,
    financial_model_valid,
    financial_model_issues,
  };
}

// ══════════════════════════════════════════════════════════════════════════════
// G2: RETURNS SCORE (0–40 pts)
// ══════════════════════════════════════════════════════════════════════════════

function scoreReturns(input: ScoringInput, m: DerivedMetrics): PillarScore {
  const inputs: PillarInput[] = [];
  const subScores: SubScore[] = [];
  let total = 0;

  // IRR Subscore (0–18)
  const irr = m.proxy_irr;
  let irrScore = 0;
  if (irr >= 18) irrScore = 18;
  else if (irr >= 16) irrScore = 16 + (irr - 16) / 2 * 2;
  else if (irr >= 14) irrScore = 13 + (irr - 14) / 2 * 3;
  else if (irr >= 12) irrScore = 9 + (irr - 12) / 2 * 4;
  else if (irr >= 10) irrScore = 5 + (irr - 10) / 2 * 4;
  else if (irr >= 0)  irrScore = (irr / 10) * 5;     // continuous 0→5 across 0–10%
  else                irrScore = 0;                  // negative IRR floors at 0

  // IRR is only "available" when computeProxyIRR produced a finite, realistic value.
  const irrAvailable = m.total_cash_invested > 0 && m.noi_stabilized > 0 && Number.isFinite(irr);
  if (irrAvailable) {
    total += irrScore;
    inputs.push({ label: "IRR (estimated)", key: "proxy_irr", provided: true, value: `${irr.toFixed(1)}%`, source: "computed", impact: "high" });
    subScores.push({ name: "IRR", key: "irr", score: irrScore, maxPoints: 18, details: `${irr.toFixed(1)}%` });
  } else {
    inputs.push({ label: "IRR (estimated)", key: "proxy_irr", provided: false, source: "missing", impact: "high", improvementHint: "Complete acquisition, income, and debt inputs to compute IRR." });
    subScores.push({ name: "IRR", key: "irr", score: 0, maxPoints: 18 });
  }

  // Cash-on-Cash Subscore (0–12)
  const coc = m.cash_on_cash;
  let cocScore = 0;
  if (coc >= 10) cocScore = 12;
  else if (coc >= 8) cocScore = 10 + (coc - 8) / 2 * 2;
  else if (coc >= 6) cocScore = 7 + (coc - 6) / 2 * 3;
  else if (coc >= 4) cocScore = 4 + (coc - 4) / 2 * 3;
  else if (coc >= 2) cocScore = 2 + (coc - 2) / 2 * 2;
  else if (coc >= 0) cocScore = (coc / 2) * 2;       // 0→2 across 0–2%
  else cocScore = 0;                                 // negative CoC floors at 0

  const cocAvailable = m.total_cash_invested > 0;
  if (cocAvailable) {
    total += cocScore;
    inputs.push({ label: "Cash-on-Cash", key: "cash_on_cash", provided: true, value: `${coc.toFixed(1)}%`, source: "computed", impact: "high" });
    subScores.push({ name: "Cash-on-Cash", key: "coc", score: cocScore, maxPoints: 12, details: `${coc.toFixed(1)}%` });
  } else {
    inputs.push({ label: "Cash-on-Cash", key: "cash_on_cash", provided: false, source: "missing", impact: "high", improvementHint: "Provide purchase price and debt terms." });
    subScores.push({ name: "Cash-on-Cash", key: "coc", score: 0, maxPoints: 12 });
  }

  // Equity Created Subscore (0–10)
  const eqPct = m.equity_created_pct;
  let eqScore = 0;
  if (eqPct >= 20) eqScore = 10;
  else if (eqPct >= 15) eqScore = 8;
  else if (eqPct >= 10) eqScore = 6;
  else if (eqPct >= 5) eqScore = 3;
  else if (eqPct >= 0) eqScore = 1;
  else eqScore = 0;

  const eqAvailable = m.value_at_stabilization > 0;
  if (eqAvailable) {
    total += eqScore;
    inputs.push({ label: "Equity Created", key: "equity_created_pct", provided: true, value: `${eqPct.toFixed(1)}%`, source: "computed", impact: "high" });
    subScores.push({ name: "Equity Created", key: "equity", score: eqScore, maxPoints: 10, details: `${eqPct.toFixed(1)}%` });
  } else {
    inputs.push({ label: "Equity Created", key: "equity_created_pct", provided: false, source: "missing", impact: "high", improvementHint: "Provide exit cap and stabilized rents." });
    subScores.push({ name: "Equity Created", key: "equity", score: 0, maxPoints: 10 });
  }

  // If IRR not available because the underlying inputs are missing entirely,
  // redistribute proportionally. But if IRR is unavailable due to stressed
  // financials (negative/zero stabilized NOI under sensitivity), keep the IRR
  // slot in the denominator so the score correctly drops rather than rebasing.
  const irrInputsPresent = m.total_cash_invested > 0 && m.noi_stabilized > 0;
  const irrSlotIncluded = irrAvailable || irrInputsPresent;
  const maxAvailable = (irrSlotIncluded ? 18 : 0) + (cocAvailable ? 12 : 0) + (eqAvailable ? 10 : 0);
  const normalized = maxAvailable > 0 ? ((total / maxAvailable) * 100) : 0;

  return {
    name: "Returns",
    key: "returns",
    score: normalized,
    rawScore: total,
    maxPossible: 40,
    weight: 0.40,
    available: maxAvailable > 0,
    inputs,
    subScores,
  };
}

// ══════════════════════════════════════════════════════════════════════════════
// G3: DEBT SAFETY SCORE (0–25 pts)
// ══════════════════════════════════════════════════════════════════════════════

function scoreDebtSafety(input: ScoringInput, m: DerivedMetrics): PillarScore {
  const inputs: PillarInput[] = [];
  const subScores: SubScore[] = [];
  let total = 0;

  const hasDebt = m.loan_amount > 0;

  // DSCR Subscore (0–15)
  if (!hasDebt) {
    total += 15; // No debt = full score
    inputs.push({ label: "DSCR", key: "dscr", provided: true, value: "N/A (no debt)", source: "computed", impact: "high" });
    subScores.push({ name: "DSCR", key: "dscr", score: 15, maxPoints: 15, details: "No debt" });
  } else {
    const dscr = m.dscr;
    let dscrScore = 0;
    if (dscr >= 1.35) dscrScore = 15;
    else if (dscr >= 1.25) dscrScore = 12 + (dscr - 1.25) / 0.10 * 3;
    else if (dscr >= 1.15) dscrScore = 8 + (dscr - 1.15) / 0.10 * 4;
    else if (dscr >= 1.05) dscrScore = 4 + (dscr - 1.05) / 0.10 * 4;
    else if (dscr >= 0.95) dscrScore = 2 + (dscr - 0.95) / 0.10 * 2;
    // Below break-even: continuous decay so further deterioration still drops score.
    // 0.95→2pts, 0.50→0pts, anything lower → 0.
    else if (dscr > 0)     dscrScore = Math.max(0, ((dscr - 0.50) / 0.45) * 2);
    else                    dscrScore = 0;
    total += dscrScore;
    inputs.push({ label: "DSCR", key: "dscr", provided: dscr > 0, value: dscr > 0 ? `${dscr.toFixed(2)}x` : undefined, source: dscr > 0 ? "computed" : "missing", impact: "high", improvementHint: dscr <= 0 ? "Enter NOI and debt terms to compute DSCR." : undefined });
    subScores.push({ name: "DSCR", key: "dscr", score: dscrScore, maxPoints: 15, details: dscr > 0 ? `${dscr.toFixed(2)}x` : "Missing" });
  }

  // Breakeven Occupancy Subscore (0–10)
  if (!hasDebt) {
    total += 10;
    inputs.push({ label: "Breakeven Occupancy", key: "breakeven_occ", provided: true, value: "N/A (no debt)", source: "computed", impact: "medium" });
    subScores.push({ name: "Breakeven Occupancy", key: "breakeven", score: 10, maxPoints: 10, details: "No debt" });
  } else {
    const be = m.breakeven_occupancy;
    let beScore = 0;
    if (be <= 75) beScore = 10;
    else if (be <= 80) beScore = 10 - (be - 75) / 5 * 2;     // 10→8
    else if (be <= 85) beScore = 8 - (be - 80) / 5 * 3;      // 8→5
    else if (be <= 90) beScore = 5 - (be - 85) / 5 * 3;      // 5→2
    else if (be <= 110) beScore = Math.max(0, 2 - (be - 90) / 20 * 2); // 2→0 across 90→110%
    else beScore = 0;
    total += beScore;
    const beAvail = be > 0 && be < 200;
    inputs.push({ label: "Breakeven Occupancy", key: "breakeven_occ", provided: beAvail, value: beAvail ? `${be.toFixed(1)}%` : undefined, source: beAvail ? "computed" : "missing", impact: "medium" });
    subScores.push({ name: "Breakeven Occupancy", key: "breakeven", score: beScore, maxPoints: 10, details: beAvail ? `${be.toFixed(1)}%` : "Missing" });
  }

  const normalized = ((total / 25) * 100);

  return {
    name: "Debt Safety",
    key: "debt_safety",
    score: normalized,
    rawScore: total,
    maxPossible: 25,
    weight: 0.25,
    available: true, // always available (no debt = full score)
    inputs,
    subScores,
  };
}

// ══════════════════════════════════════════════════════════════════════════════
// G4: EXECUTION RISK SCORE (0–20 pts)
// ══════════════════════════════════════════════════════════════════════════════

function scoreExecutionRisk(input: ScoringInput, m: DerivedMetrics): PillarScore {
  const inputs: PillarInput[] = [];
  const subScores: SubScore[] = [];
  let total = 0;

  // CapEx Realism (0–8)
  const units = input.unit_count || 0;
  const capex = input.capex_total || input.capex_reserves || 0;
  const capexPerUnit = input.capex_per_unit || (units > 0 && capex > 0 ? capex / units : 0);
  const yearBuilt = input.year_built || 0;

  if (capexPerUnit > 0 && yearBuilt > 0) {
    let bandLow: number, bandHigh: number;
    if (yearBuilt <= 1980) { bandLow = 12000; bandHigh = 25000; }
    else if (yearBuilt <= 2005) { bandLow = 8000; bandHigh = 20000; }
    else { bandLow = 5000; bandHigh = 15000; }

    let capexScore = 0;
    if (capexPerUnit >= bandLow && capexPerUnit <= bandHigh) {
      capexScore = 8;
    } else if (capexPerUnit > bandHigh) {
      capexScore = 6; // Heavy but not bad
    } else if (capexPerUnit >= bandLow * 0.75) {
      capexScore = 5; // 10-25% below
    } else {
      capexScore = 2; // >25% below
    }
    total += capexScore;
    inputs.push({ label: "CapEx/Unit", key: "capex_per_unit", provided: true, value: `$${Math.round(capexPerUnit).toLocaleString()}`, source: "computed", impact: "high" });
    subScores.push({ name: "CapEx Realism", key: "capex_realism", score: capexScore, maxPoints: 8, details: `$${Math.round(capexPerUnit).toLocaleString()}/unit (band: $${bandLow.toLocaleString()}–$${bandHigh.toLocaleString()})` });
  } else {
    inputs.push({ label: "CapEx/Unit", key: "capex_per_unit", provided: false, source: "missing", impact: "high", improvementHint: "Enter CapEx total and year built to assess renovation realism." });
    subScores.push({ name: "CapEx Realism", key: "capex_realism", score: 0, maxPoints: 8 });
  }

  // Vacancy / Lease-up (0–7)
  const occ = input.occupancy_current;
  const stabVacancy = input.vacancy_rate || 5;
  let vacScore = 0;
  if (occ != null) {
    if (occ >= 90 && stabVacancy >= 5 && stabVacancy <= 7) vacScore = 7;
    else if (occ >= 90) vacScore = 6;
    else if (occ >= 80) vacScore = 5;
    else if (occ >= 70) vacScore = 3;
    else vacScore = 1;

    // Penalty for aggressive vacancy assumption
    if (stabVacancy < 5) vacScore = Math.max(0, vacScore - 2);

    total += vacScore;
    inputs.push({ label: "Current Occupancy", key: "occupancy_current", provided: true, value: `${occ}%`, source: "context", impact: "high" });
    subScores.push({ name: "Vacancy / Lease-up", key: "vacancy", score: vacScore, maxPoints: 7, details: `${occ}% occupancy, ${stabVacancy}% stabilized vacancy` });
  } else {
    inputs.push({ label: "Current Occupancy", key: "occupancy_current", provided: false, source: "missing", impact: "high", improvementHint: "Enter current occupancy to assess lease-up risk." });
    subScores.push({ name: "Vacancy / Lease-up", key: "vacancy", score: 0, maxPoints: 7 });
  }

  // Value-Add Clarity (0–5)
  const opsCount = input.operational_value_add?.length || 0;
  const physCount = input.physical_value_add?.length || 0;
  let vaScore = 0;
  if (opsCount >= 1 && physCount >= 1) vaScore = 5;
  else if (opsCount >= 1 || physCount >= 1) vaScore = 3;
  else vaScore = 0;

  total += vaScore;
  const vaProvided = opsCount > 0 || physCount > 0;
  inputs.push({ label: "Value-Add Drivers", key: "value_add_clarity", provided: vaProvided, value: vaProvided ? `${opsCount} operational, ${physCount} physical` : undefined, source: vaProvided ? "context" : "missing", impact: "medium", improvementHint: !vaProvided ? "Select operational and physical value-add characteristics." : undefined });
  subScores.push({ name: "Value-Add Clarity", key: "value_add", score: vaScore, maxPoints: 5, details: `${opsCount} ops + ${physCount} physical` });

  const maxAvail = (capexPerUnit > 0 && yearBuilt > 0 ? 8 : 0) + (occ != null ? 7 : 0) + 5;
  const normalized = maxAvail > 0 ? ((total / maxAvail) * 100) : 0;

  return {
    name: "Execution Risk",
    key: "execution_risk",
    score: normalized,
    rawScore: total,
    maxPossible: 20,
    weight: 0.20,
    available: maxAvail > 0,
    inputs,
    subScores,
  };
}

// ══════════════════════════════════════════════════════════════════════════════
// G5: EXIT & MARKET RISK SCORE (0–15 pts)
// ══════════════════════════════════════════════════════════════════════════════

function scoreExitMarketRisk(input: ScoringInput, m: DerivedMetrics, baseScore: number): PillarScore {
  const inputs: PillarInput[] = [];
  const subScores: SubScore[] = [];
  let total = 0;

  // Exit Cap Sensitivity (0–10)
  // Compute how much the score drops if exit cap +50bps
  const exitCap = input.exit_cap || m.cap_rate_going_in;
  if (exitCap > 0 && m.noi_stabilized > 0) {
    const stressedCap = (exitCap + 0.5) / 100;
    const stressedValue = m.noi_stabilized / stressedCap;
    const totalBasis = (input.purchase_price || 0) + (input.capex_total || input.capex_reserves || 0);
    const stressedEqPct = totalBasis > 0 ? ((stressedValue - totalBasis) / totalBasis) * 100 : 0;
    const baseEqPct = m.equity_created_pct;
    const drop = baseEqPct - stressedEqPct;

    // Continuous score: 0pt drop → 10pts, 25pt drop → 0pts (linear).
    let exitScore = 10 - (Math.max(0, drop) / 25) * 10;
    if (exitScore < 0) exitScore = 0;
    if (exitScore > 10) exitScore = 10;

    total += exitScore;
    inputs.push({ label: "Exit Cap Sensitivity", key: "exit_cap_sensitivity", provided: true, value: `${drop.toFixed(1)}pt impact at +50bps`, source: "computed", impact: "high" });
    subScores.push({ name: "Exit Cap Sensitivity", key: "exit_cap", score: exitScore, maxPoints: 10, details: `${drop.toFixed(1)}pt equity drop at +50bps` });
  } else {
    // Default middle score if no exit data
    total += 5;
    inputs.push({ label: "Exit Cap", key: "exit_cap", provided: false, source: "missing", impact: "high", improvementHint: "Enter exit cap rate to assess exit risk sensitivity." });
    subScores.push({ name: "Exit Cap Sensitivity", key: "exit_cap", score: 5, maxPoints: 10 });
  }

  // Market Type (0–5)
  const mt = input.market_type;
  let mtScore = 0;
  if (mt === "primary") mtScore = 5;
  else if (mt === "secondary") mtScore = 3;
  else if (mt === "tertiary") mtScore = 1;

  if (mt) {
    total += mtScore;
    inputs.push({ label: "Market Type", key: "market_type", provided: true, value: mt, source: "context", impact: "high" });
    subScores.push({ name: "Market Type", key: "market_type", score: mtScore, maxPoints: 5, details: mt });
  } else {
    inputs.push({ label: "Market Type", key: "market_type", provided: false, source: "missing", impact: "high", improvementHint: "Select market type to weight exit assumptions." });
    subScores.push({ name: "Market Type", key: "market_type", score: 0, maxPoints: 5 });
  }

  const maxAvail = (exitCap > 0 ? 10 : 10) + (mt ? 5 : 0);
  const normalized = maxAvail > 0 ? ((total / maxAvail) * 100) : 0;

  return {
    name: "Exit & Market Risk",
    key: "exit_market_risk",
    score: normalized,
    rawScore: total,
    maxPossible: 15,
    weight: 0.15,
    available: true,
    inputs,
    subScores,
  };
}

// ══════════════════════════════════════════════════════════════════════════════
// RISK LEVEL + VERDICT
// ══════════════════════════════════════════════════════════════════════════════

function computeRiskLevel(m: DerivedMetrics, flags: RiskFlag[], sensitivityFlip: boolean): "Low" | "Medium" | "High" | "Very High" {
  const majorFlagCount = flags.filter((f) => f.severity === "major").length;
  const severeDscrStress = sensitivityFlip || (m.dscr > 0 && m.dscr < 1.0);

  // Severe break-even failure → Very High (no moderate classification possible).
  if (m.dscr > 0 && m.dscr < 0.80) return "Very High";
  if (m.breakeven_occupancy > 100) return "Very High";

  // Below break-even coverage → High (no moderate allowed).
  if (m.dscr > 0 && m.dscr < 1.0) return "High";
  if (m.dscr > 0 && m.dscr < 1.15) return "High";
  if (m.breakeven_occupancy > 85) return "High";
  if (severeDscrStress) return "High";
  if (majorFlagCount >= 4) return "High";

  if (m.dscr >= 1.25 && m.breakeven_occupancy <= 80 && !sensitivityFlip && majorFlagCount <= 2) return "Low";

  return "Medium";
}

function computeVerdict(score: number, risk: "Low" | "Medium" | "High" | "Very High", confidence: "High" | "Medium" | "Low", t: ScoringThresholds): { verdict: "GO" | "NO-GO" | "MAYBE"; label: string; note: string } {
  if (score >= t.proceed_cutoff && confidence !== "Low" && risk !== "Very High") {
    return { verdict: "GO", label: "GO", note: risk === "High" ? "Meets target thresholds but flagged as high risk — proceed with enhanced diligence." : "Meets target thresholds under the base case." };
  }
  if (score < t.caution_cutoff) {
    return { verdict: "NO-GO", label: "PASS ON DEAL", note: "Return/risk profile does not meet thresholds under current assumptions." };
  }
  return { verdict: "MAYBE", label: "PROCEED WITH CAUTION", note: "Works with diligence; sensitivities may change outcome." };
}

function computeConfidence(input: ScoringInput, m: DerivedMetrics, sensitivityStable: boolean, sensitivityFlip: boolean): ConfidenceResult {
  const details: string[] = [];

  // 1) Input Completeness (0–50)
  const groups: { name: string; complete: boolean }[] = [
    { name: "Acquisition", complete: !!(input.purchase_price) },
    { name: "Income", complete: !!(input.avg_in_place_rent || input.gross_potential_rent) },
    { name: "Expenses", complete: !!(input.total_operating_expenses || input.expense_ratio) },
    { name: "Vacancy", complete: input.vacancy_rate != null },
    { name: "Debt", complete: m.loan_amount > 0 ? !!(input.interest_rate && input.amortization) : true },
    { name: "Exit", complete: !!(input.exit_cap || m.cap_rate_going_in > 0) },
    { name: "CapEx", complete: !!(input.capex_total || input.capex_reserves || input.capex_per_unit) },
  ];
  const completeCount = groups.filter(g => g.complete).length;
  const completeness = Math.round((completeCount / groups.length) * 50);
  const missingGroups = groups.filter(g => !g.complete).map(g => g.name);
  if (missingGroups.length > 0) details.push(`Missing: ${missingGroups.join(", ")}`);

  // 2) Assumption Realism (0–30)
  let realism = 30;
  const stabVacancy = input.vacancy_rate || 5;
  if (stabVacancy < 5) { realism -= 6; details.push("Vacancy assumption aggressive (<5%)"); }

  const capexPerUnit = input.capex_per_unit || ((input.capex_total || input.capex_reserves || 0) / (input.unit_count || 1));
  const yearBuilt = input.year_built || 2000;
  const bandLow = yearBuilt <= 1980 ? 12000 : yearBuilt <= 2005 ? 8000 : 5000;
  if (capexPerUnit > 0 && capexPerUnit < bandLow * 0.75) { realism -= 8; details.push("CapEx/unit significantly below typical band"); }
  // Missing CapEx clarity reduces confidence band.
  const capexProvided = !!(input.capex_total || input.capex_reserves || input.capex_per_unit);
  if (!capexProvided) { realism -= 6; details.push("CapEx scope not provided — confidence reduced"); }

  const expRatio = m.expense_ratio;
  if (expRatio > 0 && expRatio < 35) { realism -= 6; details.push("Expense ratio below typical range"); }

  const exitCap = input.exit_cap || m.cap_rate_going_in;
  if (exitCap > 0 && exitCap < 4.5 && input.market_type !== "primary") { realism -= 6; details.push("Exit cap aggressive for market type"); }

  if (!input.stabilized_rent && !input.market_rent_estimate && !input.rent_premium_pct) { realism -= 4; details.push("Stabilized rent not clearly defined"); }

  realism = Math.max(0, realism);

  // 3) Sensitivity Stability (0–20)
  let stability = 20;
  if (sensitivityFlip) { stability = 5; details.push("Verdict flips under mild stress"); }
  else if (!sensitivityStable) { stability = 8; details.push("Score drops >15pts under mild stress"); }

  const totalScore = completeness + realism + stability;
  // Tightened bands so multiple low-confidence signals demote the level.
  // Also force a Low band when CapEx clarity AND another major source are missing.
  let level: "High" | "Medium" | "Low" = "High";
  if (totalScore < 60) level = "Low";
  else if (totalScore < 82) level = "Medium";
  if (!capexProvided && missingGroups.length >= 2 && level === "Medium") level = "Low";

  return { level, score: totalScore, completeness, realism, stability, details };
}

// ══════════════════════════════════════════════════════════════════════════════
// FLAG ENGINE
// ══════════════════════════════════════════════════════════════════════════════

function computeFlags(input: ScoringInput, m: DerivedMetrics): RiskFlag[] {
  const flags: RiskFlag[] = [];

  const stabVacancy = input.vacancy_rate || 5;
  if (stabVacancy < 5) {
    flags.push({ text: "Vacancy assumption aggressive", severity: "major", category: "Execution", nextStep: "Review tenant profile and lease expirations; confirm lease-up timeline and concessions." });
  }
  if (input.occupancy_current != null && input.occupancy_current < 80) {
    flags.push({ text: "Elevated lease-up risk", severity: "major", category: "Execution", nextStep: "Review tenant profile and lease expirations; confirm lease-up timeline and concessions." });
  }

  const capexPerUnit = input.capex_per_unit || ((input.capex_total || input.capex_reserves || 0) / (input.unit_count || 1));
  const yearBuilt = input.year_built || 2000;
  const bandLow = yearBuilt <= 1980 ? 12000 : yearBuilt <= 2005 ? 8000 : 5000;
  if (capexPerUnit > 0 && capexPerUnit < bandLow * 0.75) {
    flags.push({ text: "CapEx likely understated", severity: "major", category: "Execution", nextStep: "Confirm renovation scope with contractor bids; add contingency." });
  } else if (capexPerUnit > 0 && capexPerUnit < bandLow) {
    flags.push({ text: "CapEx may be low for vintage", severity: "minor", category: "Execution", nextStep: "Confirm renovation scope with contractor bids; add contingency." });
  }

  const expRatio = m.expense_ratio;
  if (expRatio > 0 && expRatio < 35) {
    flags.push({ text: "Expense ratio unusually low", severity: "major", category: "Returns", nextStep: "Request trailing 12 (T12) and validate expense ratio assumptions." });
  }

  if (m.dscr > 0 && m.dscr < 1.20) {
    flags.push({ text: "DSCR thin", severity: "major", category: "Debt Safety", nextStep: "Model rate sensitivity and confirm lender covenant levels." });
  }

  if (input.market_type === "tertiary") {
    flags.push({ text: "Market risk higher; validate exit assumptions", severity: "minor", category: "Exit", nextStep: "Stress-test exit cap at +75–100 bps; verify recent comparable sales." });
  }

  if (m.proxy_irr > 0 && m.proxy_irr < 12) {
    flags.push({ text: "Returns below typical value-add threshold", severity: "minor", category: "Returns", nextStep: "Validate market rents with comps within 0.5 miles; confirm achievable premiums." });
  }

  if (m.breakeven_occupancy > 85) {
    flags.push({ text: "Breakeven occupancy dangerously high", severity: "major", category: "Debt Safety", nextStep: "Review rent growth assumptions and expense reduction opportunities." });
  }

  if ((input.ltv || 0) > 75) {
    flags.push({ text: "Elevated leverage above 75% LTV", severity: "minor", category: "Debt Safety", nextStep: "Model refinance scenarios at maturity with rate sensitivity." });
  }

  return flags;
}

// ══════════════════════════════════════════════════════════════════════════════
// SENSITIVITY ENGINE
// ══════════════════════════════════════════════════════════════════════════════

function computeSensitivity(input: ScoringInput, m: DerivedMetrics, baseScore: number, t: ScoringThresholds): { scenarios: SensitivityScenario[]; stable: boolean; verdictFlip: boolean; calibration: SensitivityCalibrationEcho | null } {
  const scenarios: SensitivityScenario[] = [];

  // ── Market calibration of the stress magnitudes ──
  // Defaults are the historical standard grid; a calibrated deal overrides
  // only what its metro's data can actually support, and the echo records
  // every adjustment so the memo can say why this grid is not the stock one.
  const cal = input.market_calibration ?? null;
  let rentStressPct = 10;
  let capBpsPair: [number, number] = [50, 100];
  const calNotes: string[] = [];

  if (cal) {
    if (cal.rent_growth_yoy != null) {
      // Anchor the rent miss to observed movement: a metro already losing
      // rent gets stressed harder, a rising one is not pushed past observed
      // reality. Bounds [5, 15] keep the grid meaningful in both directions.
      const raw = 10 - cal.rent_growth_yoy;
      rentStressPct = Math.round(Math.min(15, Math.max(5, raw)) * 2) / 2;
      calNotes.push(
        `Rent stress set to −${rentStressPct}% — ${cal.metro} rents moved ${cal.rent_growth_yoy > 0 ? "+" : ""}${cal.rent_growth_yoy}% YoY.`,
      );
    }
    if (cal.cap_rate_trend === "up") {
      // Only ever deepen. A "down" trend never softens the cap stress: cap
      // expansion is the exit-killing risk, and a favourable trend reading
      // is too weak a signal to shrink the downside case over.
      capBpsPair = [75, 125];
      calNotes.push(`Exit-cap stress widened to +75/+125bps — cap rates in ${cal.metro} are trending up.`);
    }
  }
  const calibrationEcho: SensitivityCalibrationEcho | null = cal && calNotes.length > 0
    ? { metro: cal.metro, rent_stress_pct: rentStressPct, exit_cap_bps: capBpsPair, notes: calNotes }
    : null;
  const baseVerdict = baseScore >= t.proceed_cutoff ? "Proceed" : baseScore >= t.caution_cutoff ? "Caution" : "Pass";
  let verdictFlip = false;
  // Institutional break-condition: any scenario that breaks the deal financially
  // (DSCR < 1.0 or score collapses below the 20-point floor) must surface as Pass —
  // no Proceed/Caution allowed under broken conditions.
  const SCORE_BREAK_FLOOR = 20;
  const toVerdict = (s: number, dscr?: number | null) => {
    if ((dscr != null && dscr > 0 && dscr < 1.0) || s < SCORE_BREAK_FLOOR) return "Pass";
    return s >= t.proceed_cutoff ? "Proceed" : s >= t.caution_cutoff ? "Caution" : "Pass";
  };
  const exitCap = parseFloat(String(input.exit_cap)) || m.cap_rate_going_in;
  const capex = input.capex_total || input.capex_reserves || 0;

  /**
   * Run one stress scenario through the real engine.
   *
   * This grid used to approximate: a proportional nudge to the base score from
   * a hand-tuned multiplier, and a simplified IRR that was not an IRR at all.
   * It published numbers the engine had never produced — a +100bps rate stress
   * reported twelve points kinder than the truth, and scenario IRRs outside the
   * ±50% band the engine's own guards enforce.
   *
   * So: perturb the input, re-run the core, report what it actually says. Every
   * number a scenario shows is now one the engine produced, which is what lets
   * the stress case and the base case be argued about in the same terms.
   */
  const runScenario = (label: string, description: string, overrides: Partial<ScoringInput>) => {
    const stressed = computeScoredCore({ ...input, ...overrides });
    const verdict = toVerdict(stressed.overall_score, stressed.m.dscr);
    if (verdict !== baseVerdict) verdictFlip = true;
    scenarios.push({
      label,
      description,
      score: stressed.overall_score,
      irr: Number.isFinite(stressed.m.proxy_irr) ? stressed.m.proxy_irr : null,
      dscr: stressed.m.dscr,
      verdictShift: verdict,
    });
  };

  const [capBps1, capBps2] = capBpsPair;
  const capSuffix = capBpsPair[0] !== 50 && cal ? ` — ${cal.metro} cap trend is rising` : "";

  // Exit cap widening — the exit-killing risk, so it leads the grid.
  if (exitCap > 0 && m.noi_stabilized > 0) {
    for (const bps of [capBps1, capBps2]) {
      runScenario(
        `Exit Cap +${bps} bps`,
        `Cap rate to ${(exitCap + bps / 100).toFixed(2)}%${capSuffix}`,
        { exit_cap: exitCap + bps / 100 },
      );
    }
  }

  // CapEx overrun — raises both the basis and the equity cheque, so it now
  // reaches the returns pillar through total_cash_invested rather than only
  // through equity created.
  if (capex > 0) {
    for (const pct of [10, 15]) {
      runScenario(
        `CapEx +${pct}%`,
        `Budget +$${Math.round(capex * (pct / 100)).toLocaleString()}`,
        { capex_total: capex * (1 + pct / 100) },
      );
    }
  }

  // Rent premium miss — magnitude anchored to the metro when calibrated
  const stabRent = input.stabilized_rent || input.market_rent_estimate || 0;
  const inPlace = input.avg_in_place_rent || 0;
  if (stabRent > 0 && inPlace > 0) {
    const reducedRent = stabRent * (1 - rentStressPct / 100);
    const rentSuffix = rentStressPct !== 10 && cal?.rent_growth_yoy != null
      ? ` — calibrated to ${cal.metro} (${cal.rent_growth_yoy > 0 ? "+" : ""}${cal.rent_growth_yoy}% YoY)`
      : "";
    // Reduce whichever field supplied the stabilized rent, so the stress lands
    // on the same input the base case actually read.
    const rentOverride: Partial<ScoringInput> = input.stabilized_rent
      ? { stabilized_rent: reducedRent }
      : { market_rent_estimate: reducedRent };
    runScenario(
      `Rent Premium −${rentStressPct}%`,
      `Stabilized rent to $${Math.round(reducedRent)}/mo${rentSuffix}`,
      rentOverride,
    );
  }

  // Rate +100 bps
  if (m.loan_amount > 0 && input.interest_rate) {
    const rateNum = parseFloat(String(input.interest_rate)) || 0;
    runScenario(
      "Rate +100 bps",
      `Interest rate to ${(rateNum + 1).toFixed(1)}%`,
      { interest_rate: rateNum + 1 },
    );
  }

  const maxDrop = scenarios.length > 0 ? Math.max(...scenarios.map(s => baseScore - s.score)) : 0;
  const stable = maxDrop <= 15;

  return { scenarios, stable, verdictFlip, calibration: calibrationEcho };
}

// ══════════════════════════════════════════════════════════════════════════════
// DRIVERS & ACTIONS
// ══════════════════════════════════════════════════════════════════════════════

function computeDrivers(input: ScoringInput, m: DerivedMetrics, flags: RiskFlag[]): { positives: string[]; risks: string[] } {
  const positives: string[] = [];

  if (m.proxy_irr >= 16) positives.push(`Strong projected IRR at ${m.proxy_irr.toFixed(1)}%`);
  else if (m.proxy_irr >= 14) positives.push(`Solid projected IRR at ${m.proxy_irr.toFixed(1)}%`);

  if (m.cash_on_cash >= 8) positives.push(`Healthy cash-on-cash return at ${m.cash_on_cash.toFixed(1)}%`);
  if (m.dscr >= 1.30) positives.push(`Strong debt coverage at ${m.dscr.toFixed(2)}x DSCR`);
  if (m.equity_created_pct >= 15) positives.push(`Significant equity creation of ${m.equity_created_pct.toFixed(1)}%`);
  if (m.breakeven_occupancy > 0 && m.breakeven_occupancy <= 75) positives.push(`Low breakeven occupancy at ${m.breakeven_occupancy.toFixed(0)}%`);

  const rentGap = input.market_rent_estimate && input.avg_in_place_rent
    ? ((input.market_rent_estimate - input.avg_in_place_rent) / input.market_rent_estimate * 100) : 0;
  if (rentGap >= 15) positives.push(`Strong rent upside — ${rentGap.toFixed(0)}% gap to market`);
  else if (rentGap >= 8) positives.push(`Moderate rent upside — ${rentGap.toFixed(0)}% below market`);

  if (input.market_type === "primary") positives.push("Primary market positioning reduces exit risk");
  if ((input.operational_value_add?.length || 0) >= 2 && (input.physical_value_add?.length || 0) >= 2) {
    positives.push("Multiple value-add levers identified across operational and physical");
  }

  const risks = flags.map(f => f.text);

  return { positives: positives.slice(0, 5), risks: risks.slice(0, 7) };
}

function computeActions(input: ScoringInput, m: DerivedMetrics, flags: RiskFlag[]): { text: string; priority: "high" | "medium" | "low" }[] {
  const actions: { text: string; priority: "high" | "medium" | "low" }[] = [];

  // From flags
  for (const flag of flags) {
    if (flag.nextStep && flag.severity === "major") {
      actions.push({ text: flag.nextStep, priority: "high" });
    }
  }
  for (const flag of flags) {
    if (flag.nextStep && flag.severity === "minor" && actions.length < 5) {
      actions.push({ text: flag.nextStep, priority: "medium" });
    }
  }

  // Generic
  if (!input.total_operating_expenses && !input.expense_ratio) {
    actions.push({ text: "Request trailing 12 (T12) and validate expense ratio assumptions.", priority: "high" });
  }
  if (m.proxy_irr > 0) {
    actions.push({ text: "Run conservative scenario with reduced rent growth and higher vacancy.", priority: "low" });
  }

  // Deduplicate
  const seen = new Set<string>();
  return actions.filter(a => {
    if (seen.has(a.text)) return false;
    seen.add(a.text);
    return true;
  }).slice(0, 7);
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN SCORING FUNCTION
// ══════════════════════════════════════════════════════════════════════════════

/**
 * The perturbation core — everything needed to turn a deal into a score, and
 * nothing that depends on having one already.
 *
 * This exists so the stress grid can re-run the *real* engine against a
 * modified deal instead of approximating what the engine would have said. It
 * deliberately stops short of sensitivity, confidence, risk and the verdict:
 * a stress scenario needs none of them, and sensitivity would recurse.
 *
 * Nothing here is new maths. It is the front half of calculateScore, lifted
 * unchanged so that two callers can share it.
 */
interface ScoredCore {
  m: DerivedMetrics;
  pillars: PillarScore[];
  overall_score: number;
}

function computeScoredCore(input: ScoringInput): ScoredCore {
  const m = computeDerivedMetrics(input);

  // Score 4 categories
  const returns = scoreReturns(input, m);
  const debtSafety = scoreDebtSafety(input, m);
  const executionRisk = scoreExecutionRisk(input, m);

  // Need a preliminary base score for exit sensitivity calc
  const prelimScore = Math.round(
    returns.score * returns.weight +
    debtSafety.score * debtSafety.weight +
    executionRisk.score * executionRisk.weight
  );

  const exitMarket = scoreExitMarketRisk(input, m, prelimScore);

  const pillars: PillarScore[] = [returns, debtSafety, executionRisk, exitMarket];

  // Overall weighted score
  const weightedScore =
    returns.score * returns.weight +
    debtSafety.score * debtSafety.weight +
    executionRisk.score * executionRisk.weight +
    exitMarket.score * exitMarket.weight;

  // Coverage-failure penalty — scales smoothly so further DSCR / NOI deterioration
  // continues to move the overall score even when individual pillars are floored.
  // Applied only when the loan is actually present.
  let penalty = 0;
  if (m.loan_amount > 0 && m.dscr > 0) {
    if (m.dscr < 1.0) penalty += (1.0 - m.dscr) * 30;          // 0→30 across 1.0 → 0.0
    if (m.dscr < 0.80) penalty += (0.80 - m.dscr) * 25;        // additional steepening below 0.80
  }
  if (m.breakeven_occupancy > 90) {
    penalty += Math.min(15, (m.breakeven_occupancy - 90) * 0.5);
  }
  let overall_score = Math.max(0, Math.round(weightedScore - penalty));

  // ── Capital-protection score ceiling ─────────────────────────────────────
  // When a deal is financially broken, the overall score MUST collapse so it
  // cannot contradict the verdict / risk / severity layers.
  //   • DSCR < 1.0 or breakeven > 100% → score capped at 10
  //   • DSCR < 0.80 → score forced to 0 (full collapse)
  const fullyCollapsed = (m.dscr > 0 && m.dscr < 0.80) || m.breakeven_occupancy > 100;
  const broken = fullyCollapsed || (m.dscr > 0 && m.dscr < 1.0) || m.breakeven_occupancy > 95;
  if (fullyCollapsed) {
    overall_score = 0;
  } else if (broken) {
    overall_score = Math.min(overall_score, 10);
  }

  return { m, pillars, overall_score };
}

export function calculateScore(input: ScoringInput, thresholds: ScoringThresholds = DEFAULT_THRESHOLDS): ScoringResult {
  const t = thresholds;
  const { m, pillars, overall_score } = computeScoredCore(input);

  // Flags
  const flags = computeFlags(input, m);

  // Sensitivity
  const { scenarios, stable, verdictFlip, calibration: sensitivityCalibration } = computeSensitivity(input, m, overall_score, t);

  // Confidence
  const confidence = computeConfidence(input, m, stable, verdictFlip);

  // Risk Level
  const risk_level = computeRiskLevel(m, flags, verdictFlip);

  // ── GLOBAL BREAK DETECTOR (capital-protection failsafe) ─────────────────
  // Scan every sensitivity scenario. If ANY scenario breaks the deal
  // (DSCR < 1.0, score ≤ 10, Pass verdict, or "breaks" wording), the deal
  // CANNOT surface as Proceed/Caution at the final verdict. This runs before
  // verdict computation and after, as a hard failsafe.
  const SCORE_BREAK_FLOOR = 20;
  const SCORE_COLLAPSE_FLOOR = 10;
  // A scenario breaks the deal when it shows the deal cannot *survive* — not
  // merely that it underperforms.
  //
  // This used to also fire on any scenario whose verdict read "Pass", which is
  // every scenario scoring below the caution cutoff. That was tolerable while
  // the grid approximated and barely moved; now that it re-runs the engine and
  // tells the truth, it would veto sound deals for the crime of having a bad
  // downside case. Every levered real-estate deal has one — that is what a
  // stress test is for. A deal falling from 82 to 62 under a rent miss has not
  // broken; it has been stress tested, and the result belongs in risk and
  // confidence, which already consume it through `stable` and `verdictFlip`.
  //
  // What genuinely breaks a deal is losing debt coverage or collapsing
  // outright, and both of those remain absolute.
  const scenarioBreaksDeal = (s: SensitivityScenario): boolean => {
    if (s.dscr != null && s.dscr > 0 && s.dscr < 1.0) return true;
    if (s.score <= SCORE_COLLAPSE_FLOOR) return true;
    return false;
  };
  const global_break = scenarios.some(scenarioBreaksDeal);

  // Verdict — with institutional break-condition override.
  let { verdict, label: verdict_label, note } = computeVerdict(overall_score, risk_level, confidence.level, t);
  const baselineBroken =
    (m.dscr > 0 && m.dscr < 1.0) ||
    overall_score < SCORE_BREAK_FLOOR ||
    overall_score <= SCORE_COLLAPSE_FLOOR ||
    risk_level === "Very High" ||
    global_break;
  if (baselineBroken && verdict !== "NO-GO") {
    verdict = "NO-GO";
    verdict_label = "PASS ON DEAL";
    note =
      m.dscr > 0 && m.dscr < 1.0
        ? `DSCR ${m.dscr.toFixed(2)}x is below break-even debt coverage — deal does not pencil.`
        : global_break
        ? "One or more stress scenarios break this deal — capital protection rule forces NO-GO."
        : "Return/risk profile breaks under current assumptions — deal does not pencil.";
  }

  // ── Scenario-level verdict lock ─────────────────────────────────────────
  // Any scenario that meets a break condition MUST surface as "Pass" (NO).
  for (const s of scenarios) {
    if (scenarioBreaksDeal(s)) s.verdictShift = "Pass";
  }

  // ── FAILSAFE — last line of defense before result leaves the engine ─────
  if (verdict !== "NO-GO" && global_break) {
    verdict = "NO-GO";
    verdict_label = "PASS ON DEAL";
    note = "Capital-protection failsafe: a stress scenario breaks the deal — verdict locked to NO.";
  }

  // Collect missing fields
  const missing_fields = pillars.flatMap(p => p.inputs.filter(i => !i.provided));

  let verdict_note = note;
  if (missing_fields.length > 0) {
    verdict_note += ` ${missing_fields.length} input(s) missing — score can improve with additional data.`;
  }

  // Drivers & Actions
  const drivers = computeDrivers(input, m, flags);
  const actions = computeActions(input, m, flags);

  return {
    overall_score,
    overall_max: 100,
    pillars_scored: pillars.filter(p => p.available).length,
    pillars_total: 4,
    pillars,
    missing_fields,
    verdict,
    verdict_label,
    verdict_note,
    risk_level,
    confidence,
    flags,
    sensitivity: scenarios,
    sensitivity_calibration: sensitivityCalibration,
    derived: m,
    drivers,
    actions,
  };
}

// ══════════════════════════════════════════════════════════════════════════════
// INPUT BUILDER
//
// Maps a raw deal context — wizard answers plus the envelopes written by
// parse-document — onto ScoringInput. It lives here rather than in the browser
// adapter so the edge function scores the same fields from the same shapes;
// a divergence in this mapping would reintroduce the two-scores problem even
// with a shared calculation.
// ══════════════════════════════════════════════════════════════════════════════

export function buildScoringInputFromObject(ctx: Record<string, unknown>): ScoringInput {
  if (!ctx || Object.keys(ctx).length === 0) return {};

  try {
    const c = ctx as Record<string, any>;

    // The wizard persists every numeric answer as a string ("21500000"), and
    // the engine's guards use Number.isFinite, which a string never satisfies.
    // Left uncoerced, arithmetic like price/NOI still worked through implicit
    // conversion while the guarded paths silently bailed — proxy IRR came back
    // NaN and the Returns pillar scored 0 while still reporting itself as
    // available, so every real deal lost 40% of its score to a type.
    //
    // `?? undefined` rather than `|| undefined` only where zero is meaningful;
    // elsewhere the original truthiness is preserved deliberately, because a
    // zero in those fields means "left blank", not "free".
    const n = (value: unknown): number | undefined => {
      if (value == null || value === "") return undefined;
      const parsed = typeof value === "string" ? Number(value.replace(/[$,%\s]/g, "")) : Number(value);
      return Number.isFinite(parsed) ? parsed : undefined;
    };

    const input: ScoringInput = {
      // Strategy Gate
      unit_count: n(c.unit_count) || undefined,
      occupancy_current: n(c.occupancy_current) || undefined,
      year_built: n(c.year_built) || undefined,
      market_type: c.market_type || undefined,
      operational_value_add: c.operational_value_add || undefined,
      physical_value_add: c.physical_value_add || undefined,
      hold_period: c.hold_period || undefined,
      exit_strategy: c.exit_strategy || undefined,
      target_irr_band: c.target_irr_band || undefined,

      // Quick Screen financials
      purchase_price: n(c.purchase_price) || undefined,
      capex_total: n(c.capex_reserves) || n(c.capex_total) || undefined,
      capex_per_unit: n(c.capex_per_unit) || undefined,
      closing_costs_pct: n(c.closing_costs_pct) || undefined,

      avg_in_place_rent: n(c.avg_in_place_rent) || undefined,
      stabilized_rent: n(c.stabilized_rent) || undefined,
      rent_premium_pct: n(c.rent_premium_pct) || undefined,
      other_income: n(c.other_income) || undefined,
      gross_potential_rent: n(c.gross_potential_rent) || undefined,

      expense_ratio: n(c.expense_ratio) || undefined,
      total_operating_expenses: n(c.total_operating_expenses) || undefined,
      property_taxes: n(c.property_taxes) || undefined,
      insurance: n(c.insurance) || undefined,
      per_unit_expenses: n(c.per_unit_expenses) || undefined,

      vacancy_rate: n(c.vacancy_rate),

      ltv: n(c.ltv) || undefined,
      interest_rate: n(c.interest_rate) || undefined,
      amortization: n(c.amortization) || undefined,
      loan_amount: n(c.loan_amount) || undefined,

      rent_growth_annual: n(c.rent_growth_annual) ?? undefined,
      expense_growth_annual: n(c.expense_growth_annual) ?? undefined,

      exit_cap: n(c.exit_cap) || undefined,
      selling_costs_pct: n(c.selling_costs_pct) || undefined,

      // Legacy / document-extracted
      net_operating_income: n(c.net_operating_income) || undefined,
      market_rent_estimate: n(c.market_rent_estimate) || undefined,
      avg_sqft: n(c.avg_sqft) || undefined,
      capex_reserves: n(c.capex_reserves) || undefined,
      cap_rate: n(c.cap_rate) || undefined,
      dscr: n(c.dscr) || undefined,
      cash_on_cash: n(c.cash_on_cash) || undefined,
    };

    // Merge document-extracted data
    if (c.extracted_rent_roll) {
      const rr = c.extracted_rent_roll;
      if (rr.avg_in_place_rent && !input.avg_in_place_rent) input.avg_in_place_rent = rr.avg_in_place_rent;
      if (rr.market_rent_estimate && !input.market_rent_estimate) input.market_rent_estimate = rr.market_rent_estimate;
      if (rr.vacancy_rate != null && input.vacancy_rate == null) input.vacancy_rate = rr.vacancy_rate;
      if (rr.avg_sqft && !input.avg_sqft) input.avg_sqft = rr.avg_sqft;
    }

    if (c.extracted_t12) {
      const t12 = c.extracted_t12;
      if (t12.net_operating_income && !input.net_operating_income) input.net_operating_income = t12.net_operating_income;
      if (t12.total_operating_expenses && !input.total_operating_expenses) input.total_operating_expenses = t12.total_operating_expenses;
      if (t12.gross_potential_rent && !input.gross_potential_rent) input.gross_potential_rent = t12.gross_potential_rent;
      if (t12.expense_ratio != null && !input.expense_ratio) input.expense_ratio = t12.expense_ratio;
      if (t12.per_unit_expenses && !input.per_unit_expenses) input.per_unit_expenses = t12.per_unit_expenses;
      if (t12.capex_reserves && !input.capex_reserves) input.capex_reserves = t12.capex_reserves;
    }

    // ── Canonical doc ingestion (FileUploadField shape) ──
    // Documents now live under context as `t12_file` / `rent_roll_file` (object
    // or array of objects). Each entry has `extracted_data` whose nested
    // `extracted_data` (envelope) holds `summary`, `brickshare_mapping`,
    // `metrics`, etc. We promote financial truth into the scoring input so the
    // engine can compute NOI / DSCR / expense ratio properly.
    const toFileEntries = (val: unknown): Record<string, any>[] => {
      if (!val) return [];
      if (Array.isArray(val)) return val.filter((entry): entry is Record<string, any> => Boolean(entry && typeof entry === "object"));
      if (typeof val === "object") return [val as Record<string, any>];
      return [];
    };
      const num = (v: unknown): number | null => {
        const n = typeof v === "string" ? Number(v.replace(/[$,%\s,]/g, "")) : Number(v);
        return Number.isFinite(n) ? n : null;
      };

    const t12Entries = toFileEntries(c.t12_file);
    if (t12Entries.length > 0) {
      let aggIncome = 0;
      let aggExpenses = 0;
      let aggNoi = 0;
      let aggOtherIncome = 0;
      let aggVacancyDollars = 0;
      let aggGpr = 0;
      let sawT12Grounding = false;

      for (const t12Entry of t12Entries) {
        const envelope = (t12Entry.extracted_data || {}) as Record<string, any>;
      const inner = (envelope.extracted_data || envelope) as Record<string, any>;
      const summary = (inner.summary || {}) as Record<string, any>;
      const annualSummary = (inner.annual_summary || {}) as Record<string, any>;
      const bm = (inner.brickshare_mapping || envelope.brickshare_field_mapping || {}) as Record<string, any>;
      const metrics = (inner.metrics || {}) as Record<string, any>;
      const monthly = Array.isArray(inner.monthly)
        ? inner.monthly
        : Array.isArray(inner.monthly_data)
          ? inner.monthly_data
          : Array.isArray(summary.monthly_data)
            ? summary.monthly_data
            : [];

      // Aggregate from monthly rollup if summary fields are missing — guarantees
      // T12 NOI is non-zero whenever per-month data is present.
      const sumMonthly = (k: string): number => {
        let total = 0;
        for (const row of monthly) {
          const v = (row && typeof row === "object") ? (row as any)[k] : null;
          const n = typeof v === "string" ? Number(v.replace(/[$,%\s,]/g, "")) : Number(v);
          if (Number.isFinite(n)) total += n;
        }
        return total;
      };
      const monthlyGpr = monthly.length ? sumMonthly("gross_potential_rent") : 0;
      const monthlyCollected = monthly.length ? sumMonthly("collected_rent") : 0;
      const monthlyRental = monthly.length ? sumMonthly("rental_income") : 0;
      const monthlyOther = monthly.length ? sumMonthly("other_income") : 0;
      const monthlyVacancy = monthly.length ? sumMonthly("vacancy") : 0;
      const monthlyExpenses = monthly.length ? (sumMonthly("operating_expenses") || sumMonthly("total_expenses")) : 0;
      const monthlyEgi = monthly.length ? (sumMonthly("total_income") || sumMonthly("egi")) : 0;
      const monthlyNoi = monthly.length ? sumMonthly("noi") : 0;

      // GPR (gross potential rent) = rental + other income, BEFORE vacancy.
      // EGI = after vacancy. The engine treats `gross_potential_rent` as GPI and
      // applies `vacancy_rate` on top — so we must feed it GPR, not EGI.
      const t12Gpr =
        num(annualSummary.gross_potential_rent) ??
        num(summary.gross_potential_rent) ??
        num(bm.gross_potential_rent) ??
        (monthlyGpr > 0 ? monthlyGpr : monthlyCollected > 0 ? monthlyCollected + monthlyOther + monthlyVacancy : null);
      const t12Egi =
        num(annualSummary.total_income) ??
        num(summary.total_income) ??
        num(summary.effective_gross_income) ??
        (monthlyEgi > 0 ? monthlyEgi : null);
      const t12Expenses =
        num(annualSummary.total_expenses) ??
        num(summary.total_expenses) ??
        num(summary.operating_expenses) ??
        num(bm.expenses) ??
        (monthlyExpenses > 0 ? monthlyExpenses : null);
      const t12Noi =
        num(annualSummary.noi) ??
        num(summary.noi) ??
        num(summary.net_operating_income) ??
        num(bm.noi) ??
        (monthlyNoi !== 0 ? monthlyNoi : null);

      // Vacancy implied by T12 (vacancy / GPR). Falls back to EGI/GPR delta.
      let t12VacancyPct: number | null = null;
      if (t12Gpr && t12Gpr > 0) {
        const vacancyDollars =
          num(summary.vacancy) ??
          (monthlyVacancy > 0 ? monthlyVacancy : null) ??
          (t12Egi != null ? Math.max(0, t12Gpr - t12Egi) : null);
        if (vacancyDollars != null) t12VacancyPct = (vacancyDollars / t12Gpr) * 100;
      }

      // expense_ratio: prefer derived from grounded $ over mapping floats.
      let t12ExpRatio: number | null = null;
      if (t12Expenses && (t12Egi || t12Gpr)) {
        const denom = t12Egi ?? t12Gpr ?? 0;
        if (denom > 0) t12ExpRatio = (t12Expenses / denom) * 100;
      }
      if (t12ExpRatio == null) {
        let raw = num(bm.expense_ratio) ?? num(metrics.expense_ratio);
        if (raw != null && raw > 0 && raw <= 1) raw = raw * 100;
        t12ExpRatio = raw;
      }

        if (t12Egi != null && t12Egi > 0) {
          aggIncome += t12Egi;
          sawT12Grounding = true;
        }
        if (t12Expenses != null && t12Expenses > 0) {
          aggExpenses += t12Expenses;
          sawT12Grounding = true;
        }
        if (t12Noi != null && t12Noi > 0) aggNoi += t12Noi;
        if (monthlyOther > 0) aggOtherIncome += monthlyOther;
        else if ((num(summary.other_income) ?? 0) > 0) aggOtherIncome += num(summary.other_income) ?? 0;
        if (t12Gpr != null && t12Gpr > 0) aggGpr += t12Gpr;
        if (t12VacancyPct != null && t12Gpr != null && t12Gpr > 0) {
          aggVacancyDollars += t12Gpr * (t12VacancyPct / 100);
        }
        if (t12ExpRatio != null && !isPositiveFinite(input.expense_ratio)) input.expense_ratio = t12ExpRatio;
      }

      if (sawT12Grounding) {
        if (aggIncome > 0) input.baseline_total_income = aggIncome;
        if (aggExpenses > 0) {
          input.baseline_expenses = aggExpenses;
          if (!isPositiveFinite(input.total_operating_expenses)) input.total_operating_expenses = aggExpenses;
        }
        const groundedNoi = aggIncome > 0 && aggExpenses > 0 ? aggIncome - aggExpenses : aggNoi;
        if (groundedNoi > 0) input.net_operating_income = groundedNoi;
        if (aggOtherIncome > 0 && !isPositiveFinite(input.other_income)) input.other_income = aggOtherIncome;
        if (aggGpr > 0 && !isPositiveFinite(input.gross_potential_rent)) input.gross_potential_rent = aggGpr;
        if (aggIncome > 0 && aggExpenses > 0) input.expense_ratio = (aggExpenses / aggIncome) * 100;
        if (aggGpr > 0 && aggVacancyDollars > 0 && input.vacancy_rate == null) input.vacancy_rate = (aggVacancyDollars / aggGpr) * 100;
      }
    }

    const rrEntry = toFileEntries(c.rent_roll_file)[0] ?? null;
    if (rrEntry) {
      const envelope = (rrEntry.extracted_data || {}) as Record<string, any>;
      const inner = (envelope.extracted_data || envelope) as Record<string, any>;
      const summary = (inner.summary || {}) as Record<string, any>;
      const bm = (inner.brickshare_mapping || envelope.brickshare_field_mapping || {}) as Record<string, any>;

      const rrAvgRent = num(summary.avg_in_place_rent) ?? num(bm.avg_rent);
      const rrMarketRent = num(summary.avg_market_rent);
      const rrTotalUnits = num(summary.total_units) ?? num(bm.total_units);
      const rrOccupancy = num(summary.occupancy_rate) ?? num(bm.current_occupancy);
      const rrSuggestedVacancy = num(bm.suggested_vacancy);

      if (rrAvgRent && !input.avg_in_place_rent) input.avg_in_place_rent = rrAvgRent;
      if (rrMarketRent && !input.market_rent_estimate) input.market_rent_estimate = rrMarketRent;
      if (rrTotalUnits && !input.unit_count) input.unit_count = rrTotalUnits;
      // Vacancy: convert occupancy (0–1) to vacancy %; prefer suggested vacancy from mapping.
      if (input.vacancy_rate == null) {
        if (rrSuggestedVacancy != null) {
          input.vacancy_rate = rrSuggestedVacancy <= 1 ? rrSuggestedVacancy * 100 : rrSuggestedVacancy;
        } else if (rrOccupancy != null) {
          const occPct = rrOccupancy <= 1 ? rrOccupancy * 100 : rrOccupancy;
          input.vacancy_rate = Math.max(0, 100 - occPct);
        }
      }
    }

    return input;
  } catch {
    return {};
  }
}
